/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {
  Device,
  Devices,
  Entity
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {
  deleteAdminDevice,
  deleteAdminDevices,
  deleteDevice,
  deleteDevices
} from './state';

// check selected device is keyfob device or not
const checkKeyFobDevice = (device: Device) => {
  return device?.architecture?.toLowerCase() === 'redtail';
};

export interface useAssociatedAssetsProps {
  result?: Device | Device[] | null;
  bulkDeleteResult?: Device | Device[] | null;
  error?: ServiceError | null;
  bulkDeleteError?: ServiceError | null;
  selected?: Device | null;
  bulkDeleteSelected?: Devices | null;
  isEditing?: boolean;
  bulkDeleteIsEditing?: boolean;
  checkAssetAssociation?: boolean;
}

export const useAssociatedAssets = ({
  selected,
  result,
  error,
  bulkDeleteSelected,
  bulkDeleteResult,
  bulkDeleteError,
  isEditing = false,
  bulkDeleteIsEditing = false,
  checkAssetAssociation = false
}: useAssociatedAssetsProps) => {
  const dispatch = useDispatch();
  const [associatedDevices, setAssociatedDevices]               = useState([]);
  const [unassociatedDevices, setUnassociatedDevices]            = useState([]);
  const [showBulkDeleteError, setShowBulkDeleteError]           = useState<boolean>(false);
  const [showAssociatedAssetError, setShowAssociatedAssetError] = useState<boolean>(false);

  useEffect(() => {
    setAssociatedDevices([]);
    setUnassociatedDevices([]);
    setShowBulkDeleteError(false);
    setShowAssociatedAssetError(false);
  }, [result, bulkDeleteResult, error, bulkDeleteError]);

  // Return associated and unassociated asset from the selected devices
  const getAssociatedAssets = useCallback((devices: Devices) => {
    const associated = [];
    const unassociated = [];
    devices?.forEach(device => {
      if (device?.asset !== null && !checkKeyFobDevice(device)) {
        associated.push(device);
      } else {
        unassociated.push(device);
      }
    });
    setAssociatedDevices(associated);
    setUnassociatedDevices(unassociated);
    return {
      associated,
      unassociated
    };
  }, []);

  const associatedTitle = function() {
    if (selected && isEditing) {
      return translate('common:editors.delete.title', {entity: Entity.device});
    }
    if (bulkDeleteSelected && bulkDeleteIsEditing) {
      return translate('common:editors.delete.title', {
        entity : translate('common:entityLabels.devices')
      });
    }
  };

  const getAssociatedMessage = function() {
    if (selected && isEditing) {
      return translate('admin-devices:dialog.delete.singleAssetAssociatedMessage', {entityName: selected?.name});
    }
    if (bulkDeleteSelected && bulkDeleteIsEditing) {
      const count = bulkDeleteSelected?.length;
      if (count === associatedDevices?.length) {
        return translate('admin-devices:dialog.delete.multipleAssetsAssociatedMessage', {count});
      }
      return translate('admin-devices:dialog.delete.someAssetsAssociatedMessage');
    }
  };

  const onDismiss = function() {
    if (selected && !bulkDeleteSelected) {
      dispatch(deleteAdminDevice(selected, false));
    }
    if (!selected && bulkDeleteSelected) {
      dispatch(deleteAdminDevices(bulkDeleteSelected, false));
      setShowBulkDeleteError(false);
    }
    setShowAssociatedAssetError(false);
    setAssociatedDevices([]);
    setUnassociatedDevices([]);
  };

  const onDelete = function() {
    const {associated: associatedArr} = getAssociatedAssets([selected]);
    if (associatedArr?.length && checkAssetAssociation) {
      setShowAssociatedAssetError(true);
    } else {
      dispatch(deleteDevice(selected));
    }
  };

  const onBulkDelete = function() {
    const {associated: associatedArr, unassociated: unassociatedArr} = getAssociatedAssets(bulkDeleteSelected);
    if (associatedArr?.length && !showBulkDeleteError && checkAssetAssociation) {
      if (associatedArr?.length === bulkDeleteSelected?.length) {
        setShowAssociatedAssetError(true);
      } else {
        setShowBulkDeleteError(true);
      }
    } else {
      dispatch(deleteDevices(unassociatedArr));
    }
  };

  return {
    showAssociatedAssetError,
    showBulkDeleteError,
    unassociatedDevices,
    associatedTitle,
    getAssociatedMessage,
    onBulkDelete,
    onDelete,
    onDismiss
  };
};

export default useAssociatedAssets;
